import { forwardRef, useImperativeHandle, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Search, X } from 'lucide-react';
import { cn } from '@/shared/lib/cn';

export interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /**
   * Vurgulayan arama (#456): eşleşme sayısı ve o an kaçıncısında olunduğu (0 tabanlı). Verilirse
   * metin varken "n/m" sayacı ve ⌃ ⌄ okları çıkar; Enter sonraki, Shift+Enter önceki eşleşme.
   * Gezinme döngüseldir — dönüş hesabı çağıranın (`onNext`/`onPrev` sonraki/önceki indeksi
   * kendi listesinin boyuna göre sarar).
   */
  hits?: { count: number; index: number; onNext: () => void; onPrev: () => void };
  /**
   * Metin boşken Esc. Esc önce metni temizler (olay yayılmaz — pencere kapanmaz); kutu boşsa
   * bu çağrılır, verilmemişse olay yayılır (ör. diyalog kendi Esc'iyle kapanır).
   */
  onEscape?: () => void;
  /** Sağ kontrollerden önce ek öğe (ör. arama sözdizimi ipucu ⓘ). */
  hint?: ReactNode;
  autoFocus?: boolean;
  onBlur?: () => void;
  /** Dış kutunun sınıfı — genişlik burada verilir (ör. `w-60 focus-within:w-80`). */
  className?: string;
  /** Kutu yüksekliği: grid araç çubuğu `sm` (h-8), pencereler `md` (h-9). */
  size?: 'sm' | 'md';
}

export interface SearchFieldHandle {
  focus: () => void;
}

/**
 * Ortak arama kutusu (#456): grid araması ve Sütunlar penceresi aynı davranışı paylaşır.
 *
 * Sayaç, oklar ve × kutunun İÇİNDE ama metin alanının KARDEŞİ — eskiden mutlak konumlu
 * duruyorlardı ve uzun metin altlarına giriyordu. Artık metin alanı daralır, imleç hep
 * görünür kalır (tarayıcı metni yatayda kaydırır); odak dışındayken taşan kısım `…` ile
 * kesilir, tam metin ipucunda görünür. Sayaç ve oklar yalnız metin varken yer kaplar.
 */
export const SearchField = forwardRef<SearchFieldHandle, SearchFieldProps>(function SearchField(p, ref) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  useImperativeHandle(ref, () => ({ focus: () => input.current?.focus() }));

  const hasText = p.value !== '';
  const hits = hasText ? p.hits : undefined;
  const small = p.size === 'sm';
  const btn = 'text-fg-subtle hover:text-fg hover:bg-bg-hover shrink-0 rounded p-0.5';

  return (
    <div
      className={cn(
        'border-border bg-bg focus-within:border-brand-500 flex min-w-0 items-center gap-0.5 rounded-md border pr-1 transition-[width] duration-150',
        small ? 'h-8' : 'h-9',
        p.className,
      )}
      data-search-field
    >
      <Search className={cn('text-fg-subtle ml-2 shrink-0', small ? 'h-3.5 w-3.5' : 'h-4 w-4')} />
      <input
        ref={input}
        value={p.value}
        autoFocus={p.autoFocus}
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => p.onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          p.onBlur?.();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            if (hasText) {
              e.preventDefault();
              e.stopPropagation();
              p.onChange('');
            } else if (p.onEscape) {
              e.preventDefault();
              e.stopPropagation();
              p.onEscape();
            }
          } else if (e.key === 'Enter' && p.hits) {
            // Enter sonraki, Shift+Enter önceki eşleşme — tarayıcı aramasıyla aynı his.
            e.preventDefault();
            if (!p.hits.count) return;
            if (e.shiftKey) p.hits.onPrev();
            else p.hits.onNext();
          }
        }}
        // Odak dışındayken taşan metin "…" ile kesilir; tamamı ipucunda.
        title={!focused && hasText ? p.value : undefined}
        placeholder={p.placeholder}
        aria-label={p.placeholder}
        className={cn(
          'h-full min-w-0 flex-1 bg-transparent pr-1 pl-1.5 focus-visible:outline-none',
          'text-sm',
          !focused && 'text-ellipsis',
        )}
      />
      {p.hint}
      {hits ? (
        <>
          <span
            className={cn(
              'shrink-0 px-1 text-[11px] whitespace-nowrap tabular-nums',
              hits.count > 0 ? 'text-fg-muted' : 'text-danger',
            )}
            aria-live="polite"
          >
            {hits.count > 0 ? `${Math.min(hits.index, hits.count - 1) + 1}/${hits.count}` : t('search.noHit')}
          </span>
          <button
            type="button"
            aria-label={t('search.prev')}
            title={`${t('search.prev')} (Shift+Enter)`}
            disabled={!hits.count}
            onMouseDown={(e) => e.preventDefault()}
            onClick={hits.onPrev}
            className={cn(btn, 'disabled:opacity-40')}
          >
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label={t('search.next')}
            title={`${t('search.next')} (Enter)`}
            disabled={!hits.count}
            onMouseDown={(e) => e.preventDefault()}
            onClick={hits.onNext}
            className={cn(btn, 'disabled:opacity-40')}
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
        </>
      ) : null}
      {hasText || p.onEscape ? (
        <button
          type="button"
          aria-label={t('search.clear')}
          title={`${t('search.clear')} (Esc)`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            if (hasText) {
              p.onChange('');
              input.current?.focus();
            } else p.onEscape?.();
          }}
          className={btn}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  );
});
